import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ShoppingBag, Package, Truck, Shield, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setFeaturedProducts(response.data.slice(0, 6));
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (product) => {
    try {
      await addToCart(product, 1);
      toast.success('Added to cart!');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#FFE5DD] via-[#FFF5E6] to-[#E0F7FF] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6" data-testid="hero-title">
              Your One-Stop Shop for
              <span className="block mt-2 bg-gradient-to-r from-[#FF6B35] to-[#00D9FF] bg-clip-text text-transparent">
                Everything You Need
              </span>
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Discover amazing products in electronics, gadgets, fashion, and accessories. Fast shipping, great prices!
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/products" data-testid="shop-now-button">
                <Button className="bg-[#FF6B35] hover:bg-[#ff8c5a] text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all">
                  Shop Now <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-10 sm:py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {[
              { id: 'quality', icon: Package, title: 'Quality Products', desc: 'Curated selection of top items', color: '#FF6B35' },
              { id: 'shipping', icon: Truck, title: 'Fast Shipping', desc: 'Quick delivery to your door', color: '#00D9FF' },
              { id: 'payment', icon: Shield, title: 'Secure Payment', desc: 'Safe & encrypted transactions', color: '#FFB800' },
              { id: 'returns', icon: ShoppingBag, title: 'Easy Returns', desc: '30-day return policy', color: '#10B981' }
            ].map((feature) => (
              <Card key={feature.id} className="p-4 sm:p-6 text-center hover:shadow-lg transition-shadow border-0 shadow-md" data-testid={`feature-${feature.id}`}>
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full mb-3 sm:mb-4" style={{ backgroundColor: `${feature.color}15` }}>
                  <feature.icon className="h-6 w-6 sm:h-7 sm:w-7" style={{ color: feature.color }} />
                </div>
                <h3 className="text-sm sm:text-base md:text-lg font-semibold mb-1 sm:mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-xs sm:text-sm">{feature.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-10 sm:py-12 md:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12" data-testid="categories-title">Shop by Category</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { name: 'Electronics', color: '#00D9FF', bg: 'rgba(0, 217, 255, 0.15)' },
              { name: 'Gadgets', color: '#FFB800', bg: 'rgba(255, 184, 0, 0.15)' },
              { name: 'Fashion', color: '#FF6B35', bg: 'rgba(255, 107, 53, 0.15)' },
              { name: 'Accessories', color: '#10B981', bg: 'rgba(16, 185, 129, 0.15)' }
            ].map((category) => (
              <Link key={category.name} to={`/products?category=${category.name}`} data-testid={`category-${category.name.toLowerCase()}`}>
                <Card className="p-6 sm:p-8 text-center hover:shadow-xl transition-all border-0 shadow-md cursor-pointer min-h-[80px] sm:min-h-[100px] flex items-center justify-center" style={{ background: category.bg }}>
                  <h3 className="text-base sm:text-lg md:text-xl font-bold" style={{ color: category.color }}>{category.name}</h3>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-10 sm:py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6 sm:mb-8 md:mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold" data-testid="featured-products-title">Featured Products</h2>
            <Link to="/products" className="hidden sm:block">
              <Button variant="outline" className="rounded-full border-[#FF6B35] text-[#FF6B35] hover:bg-[#FF6B35] hover:text-white min-h-[44px]">
                View All <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-12 sm:py-20">
              <div className="spinner"></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8" data-testid="featured-products-grid">
                {featuredProducts.map((product) => (
                  <Card key={product.id} className="overflow-hidden hover:shadow-xl transition-all border-0 shadow-md product-card" data-testid={`product-${product.id}`}>
                    <Link to={`/products/${product.id}`}>
                      <img src={product.image_url} alt={product.name} className="w-full h-48 sm:h-56 object-cover" />
                    </Link>
                    <div className="p-4 sm:p-6">
                      <span className={`category-badge category-${product.category.toLowerCase()} text-xs sm:text-sm`}>
                        {product.category}
                      </span>
                      <Link to={`/products/${product.id}`}>
                        <h3 className="text-base sm:text-lg font-semibold mt-2 sm:mt-3 mb-2 hover:text-[#FF6B35] transition-colors line-clamp-2">
                          {product.name}
                        </h3>
                      </Link>
                      <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2">{product.description}</p>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xl sm:text-2xl font-bold text-[#FF6B35]">${product.price.toFixed(2)}</span>
                        <Button 
                          onClick={() => handleAddToCart(product)}
                          className="bg-[#FF6B35] hover:bg-[#ff8c5a] text-white rounded-full px-4 sm:px-6 py-2 text-sm sm:text-base min-h-[44px]"
                          data-testid={`add-to-cart-${product.id}`}
                        >
                          Add
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              <Link to="/products" className="block sm:hidden mt-6">
                <Button className="w-full bg-[#FF6B35] hover:bg-[#ff8c5a] text-white rounded-full min-h-[48px]">
                  View All Products
                </Button>
              </Link>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
